import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { CircleMarker, Marker, Popup } from 'react-leaflet';
import type { IStopArea } from '../../../../models/stop-area.model';
import type { IStopPoint } from '../../../../models/stop-point.model';
import Colors from '../../../../helpers/colors.helper';
import { ButtonDivIcon } from '../../../Shared/Map/markers/button-div-icon';
import { MarkerHelper } from '../../../../helpers/marker.helper';

interface IStopLocationMarkerProps {
  stopLocation: IStopArea | IStopPoint;
  highlighted?: boolean;
  backgroundColor?: string;
  foregroundColor?: string;
  highlightedRadius?: number;
}

export default function StopLocationMarker({
  stopLocation,
  highlighted = false,
  backgroundColor = Colors.vtdBlue1000,
  foregroundColor = Colors.vtdWhite,
  highlightedRadius = 10,
}: IStopLocationMarkerProps) {
  const hitAreaMarkerRef = useRef<L.CircleMarker | null>(null);
  const markerRef = useRef<L.CircleMarker | null>(null);

  useEffect(() => {
    if (highlighted) return;

    hitAreaMarkerRef.current?.bringToBack();
    markerRef.current?.bringToBack();
  }, [highlighted, stopLocation.gid, stopLocation.latitude, stopLocation.longitude]);

  if (stopLocation.latitude === undefined || stopLocation.longitude === undefined) return null;

  const center: [number, number] = [stopLocation.latitude, stopLocation.longitude];
  const highlightedIcon = highlighted
    ? (() => {
        const circleMarkerSvg = MarkerHelper.getCircleMarkerSvg(highlightedRadius, 3, foregroundColor, backgroundColor, 1);
        return new ButtonDivIcon(
          {
            className: 'button-div-icon call-marker',
            iconSize: [circleMarkerSvg.width, circleMarkerSvg.height],
            iconAnchor: [circleMarkerSvg.width / 2, circleMarkerSvg.height / 2],
            html: circleMarkerSvg.svg,
          },
          stopLocation.name,
        );
      })()
    : undefined;

  if (highlighted && highlightedIcon) {
    return (
      <Marker position={center} icon={highlightedIcon} zIndexOffset={1100}>
        <Popup offset={[0, -4]} closeButton={false}>
          {stopLocation.name}
        </Popup>
      </Marker>
    );
  }

  // Two circle markers are used: an invisible outer one (radius 22 = 44px) provides a WCAG 2.5.5
  // compliant click target, while the inner one renders the visible dot and hosts the popup.
  // The outer circle opens the popup programmatically to ensure it is always centered on the dot.
  return (
    <>
      <CircleMarker
        ref={hitAreaMarkerRef}
        center={center}
        radius={22}
        fillOpacity={0}
        stroke={false}
        eventHandlers={{
          click: (event) => {
            L.DomEvent.stopPropagation(event.originalEvent);
            markerRef.current?.openPopup();
          },
        }}
      />
      <CircleMarker ref={markerRef} center={center} radius={6} fillColor={Colors.vtdWhite} fillOpacity={1} color={Colors.vtdGray700} weight={2} interactive={false}>
        <Popup offset={[0, -4]} closeButton={false}>
          {stopLocation.name}
        </Popup>
      </CircleMarker>
    </>
  );
}
